import { Box } from '@hivebrite/design-system/Box';
import type { EmbedType } from '@hivebrite/design-system/Embed';
import { IconVideo } from '@hivebrite/design-system/Icon';
import { Loader } from '@hivebrite/design-system/Loader';
import { useEmbedLy, useMeasure } from '@hivebrite/react-hooks';

import BuilderThemeProvider, {
  VIEW_AS,
} from 'hb-builder/components/common/BuilderThemeProvider';
import Drop from 'hb-builder/components/common/Drop';
import ImageSetter from 'hb-builder/components/common/ImageSetter';
import EmptyState from 'hb-builder/components/common/state/EmptyState';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { Wrapper, WrapperBuilder } from 'hb-builder/components/common/Wrapper';
import { defaultTabs } from 'hb-builder/constants/tabs';
import {
  useComponentContext,
  useRendererContext,
  useSectionContext,
} from 'hb-builder/hooks';
import { getJustifyContent } from 'hb-builder/utils/attributes';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { VideoItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/videoItemAttributes';
import { VIDEO_TYPE_ATTRIBUTE } from 'hb-react/backoffice/types/builder/itemAttributes/videoItemAttributes';
import Embed from 'hb-react/shared/components/DesignSystem/Embed';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import mergeRefs from 'hb-react/shared/utils/mergeRefs';

import VideoNewUI from './components/VideoNewUI';
import VideoSettings from './VideoSettings';

const componentType = 'single';

const Video = () => <div />; // never used

const VideoUI = ({ isDisabled, type, url, videoImageSrc }) => {
  const { nbFinalChildren } = useSectionContext();
  const isSectionFullWidth = nbFinalChildren === 1;

  const { response, isLoading } = useEmbedLy(url, {
    apiUrl: `${window.location.origin}${Routes.embed_path()}`,
  });

  const embed = response as EmbedType;

  if (!response && (type === VIDEO_TYPE_ATTRIBUTE.LINK || !type)) {
    return (
      <Box justifyContent="center" height={100} width="100%">
        {isLoading ? <Loader size={32} /> : <div />}
      </Box>
    );
  }

  if (videoImageSrc || type !== VIDEO_TYPE_ATTRIBUTE.LINK) {
    return (
      <VideoNewUI
        isDisabled={isDisabled}
        type={type}
        url={url}
        videoImageSrc={videoImageSrc}
        embed={embed}
        isLoading={isLoading}
      />
    );
  }

  return (
    <Embed
      autoplay
      embed={embed}
      mediaAspectRatio={isSectionFullWidth ? undefined : 1.777778}
      withDescription={false}
      isDisabled={isDisabled}
      isLoading={isLoading}
      roundCorners="none"
    />
  );
};

const RenderBuilder = () => {
  const { item } = useComponentContext();
  const { flagBuilderUploadVideo } = useFeatureFlag();
  const attributes = item?.attributes;
  const [ref, { width: wrapperWidth }] = useMeasure();
  const { nbFinalChildren, sectionHeight } = useSectionContext();

  const isNarrow = wrapperWidth < 200;

  const isEmpty = !attributes?.videoUrl;

  const renderComponent = (isOver: boolean) =>
    isEmpty ? (
      <EmptyState
        isOver={isOver}
        nbFinalChildren={nbFinalChildren}
        p={isNarrow ? 0 : 4}
        sectionHeight={sectionHeight}
      >
        <Box
          alignItems="center"
          color="primary"
          flexDirection="column"
          textAlign="center"
        >
          <IconVideo size={50} />
          {!isNarrow && (
            <Box color="black" mt={1}>
              {__('Set your video in the floating panel')}
            </Box>
          )}
        </Box>
      </EmptyState>
    ) : (
      <BuilderThemeProvider viewAs={VIEW_AS.USER}>
        <VideoUI
          isDisabled
          type={attributes.type}
          url={attributes.videoUrl}
          videoImageSrc={attributes.videoImageSrc}
        />
      </BuilderThemeProvider>
    );

  return (
    <>
      <Drop accept={['single']}>
        {({ isOver, ref: dropRef }) => (
          // @ts-expect-error untyped
          <WrapperBuilder
            attributes={attributes}
            dataTestId="builder-component-video"
            isEmpty={isEmpty}
            ref={mergeRefs([ref, dropRef])}
            style={{
              // Safari's default setting for the align-items is stretch
              alignItems: 'flex-start',
              justifyContent: getJustifyContent(attributes?.horizontalAlign),
            }}
          >
            {({ isDragging }) => (isDragging ? null : renderComponent(isOver))}
          </WrapperBuilder>
        )}
      </Drop>
      {flagBuilderUploadVideo && (
        <ImageSetter attributeName="videoImageSrc" item={item} />
      )}
    </>
  );
};

const Render = ({ item }: RenderProps<DocumentItem<VideoItemAttributes>>) => {
  const { isMobilePreview } = useRendererContext();
  const [embedRef] = useMeasure();

  const { attributes } = item || {};

  if (!attributes?.videoUrl) {
    return null;
  }

  return (
    // @ts-expect-error untyped
    <Wrapper
      attributes={attributes}
      componentType={componentType}
      dataTestId="builder-component-video"
      isMobilePreview={isMobilePreview}
      ref={embedRef}
      style={{
        // Safari's default setting for the align-items is stretch
        alignItems: 'flex-start',
        justifyContent: getJustifyContent(attributes?.horizontalAlign),
      }}
    >
      <VideoUI
        isDisabled={false}
        type={attributes.type}
        url={attributes.videoUrl}
        videoImageSrc={attributes.videoImageSrc}
      />
    </Wrapper>
  );
};

Video.componentName = 'Video';
Video.componentType = componentType;

Video.componentLabel = __('Video');
Video.componentCategory = 'single';
Video.componentIcon = () => IconVideo;

Video.canBeDoubleClicked = true;
Video.canBeDragged = true;
Video.canBeListed = true;
Video.canBeSelected = true;

Video.item = () => ({
  attributes: {
    videoUrl: '',
    type: VIDEO_TYPE_ATTRIBUTE.LINK,
  },
  category: Video.componentCategory,
  name: Video.componentName,
  type: Video.componentName,
});

Video.toolbar = ['edit', 'drag', 'duplicate', 'delete'];

Video.Settings = VideoSettings;
Video.SettingsTabs = defaultTabs;

Video.RenderBuilder = RenderBuilder;
Video.Render = Render;

export default Video;
